import { v4 as uuidv4 } from 'uuid';
import { CLFBaselines } from './data/CLFBaselines';

export type MaterialSourceSettings = {
  source: string,
  gwp: number,
  units: string,
  density: number,
  notes: string
}

export type MaterialSourceOption = {
  id: string,
  name: string,
  shortName: string,
  value: string,
}

export type MaterialInfo = {
  guid: string,
  category: string,
  name: string,
  source: string,
  gwp: number,
  units: string,
  density: number, // lb per ft3
  transparent: boolean,
  notes: string
}

export const defaultMaterialInfo: MaterialInfo = {
  guid: '',
  category: '',
  name: '',
  source: '',
  gwp: 0,
  units: 'm3',
  density: 0, // lb per ft3
  transparent: false,
  notes: ''
}

export const sourceOptions: MaterialSourceOption[] = [
  { id: '0', name: 'Select a data source', shortName: 'none', value: 'none'},
  { id: '1', name: '2023 CLF Baselines', shortName: 'CLF', value: 'gwp'},
  { id: '2', name: 'Custom...', shortName: 'Custom', value: 'custom'},
]

export const materialCategories: string[] = [
  '00 Other',
  '03 Concrete',
  '04 Masonry',
  '05 Metals',
  '06 Wood & Plastic',
  '07 Thermal & Moisture Protection',
  '08 Openings',
  '09 Finishes',
]

export class Material {
  protected className: string
  guid: string
  category: string
  name: string
  source: string
  gwpByArea: number
  gwpByVolume: number
  gwpByWeight: number
  density: number
  transparent: boolean
  notes: string

  get units(): string {
    if (this.isEmpty()) {
      return ''
    }
    if (this.gwpByVolume + this.gwpByWeight === 0) {
      return 'm2'
    }
    if (this.gwpByArea + this.gwpByWeight === 0) {
      return 'm3'
    }
    if (this.gwpByArea + this.gwpByVolume === 0) {
      return 'kg'
    }
    else return ''
  }
  get gwp(): number {
    const units = this.units
    switch (units) {
      case 'm2':
        return this.gwpByArea * 10.7639
      case 'm3':
        return this.gwpByVolume * 35.3147
      case 'kg':
        return this.gwpByWeight * 2.20462
      default:
        return 0
    }
  }

  get nameInfo(): string {
    if (this.isEmpty()) {
      return this.name
    }
    return `${this.name} -- (${this.gwp.toFixed(1)} kgCO2e/${this.units})`
  }

  get info(): MaterialInfo {
    let info: MaterialInfo = {
      guid: this.guid,
      category: this.category,
      name: this.name,
      source: this.source,
      gwp: this.gwp,
      units: this.units,
      density: this.density,
      transparent: this.transparent,
      notes: this.notes
    }
    return info
  }

  get opacity(): number {
    if (this.transparent) {
      return 0.6
    } else {
      return 1
    }
  }

  constructor(
      category: string,
      name: string,
      source: string,
      gwpByArea: number = 0,
      gwpByVolume: number = 0,
      gwpByWeight: number = 0,
      density: number = 0,
      transparent: boolean = false,
      notes: string = ''
    ) {
    this.className = 'Material'
    this.guid = uuidv4()
    this.category = category
    this.name = name
    this.source = source

    //input is gwp/m2, divide by 10.7 to get gwp/ft2
    this.gwpByArea = gwpByArea / 10.7639;

    //input is gwp/m3, divide by 35.3 to get gwp/ft3
    this.gwpByVolume = gwpByVolume / 35.3147;

    //input is gwp/kg, divide by 2.2 to get gwp/lb
    this.gwpByWeight = gwpByWeight / 2.20462;

    //input as lb/ft3, no conversion
    this.density = density;
    this.transparent = transparent
    this.notes = notes
  }

  updateMaterial(info: MaterialInfo){
    if(Material.infoMatchesBaseline(info)){
      this.source = 'CLF'
    }
    else{
      this.source = 'Custom'
    }
    this.name = info.name
    this.category = info.category
    this.setGwp(info.units, info.gwp, info.density)
    this.transparent = info.transparent
    this.notes = info.notes
  }

  setGwp(units: string, gwp: number, density = 0){
    switch (units) {
      case 'm2':
        //input is gwp/m2, divide by 10.7 to get gwp/ft2
        this.gwpByArea = gwp / 10.7639
        this.gwpByVolume = 0
        this.gwpByWeight = 0
        this.density = density
        break
      case 'm3':
        //input is gwp/m3, divide by 35.3 to get gwp/ft3
        this.gwpByArea = 0
        this.gwpByVolume = gwp / 35.3147
        this.gwpByWeight = 0
        this.density = density
        break
      case 'kg':
        //input is gwp/kg, divide by 2.2 to get gwp/lb
        this.gwpByArea = 0
        this.gwpByVolume = 0
        this.gwpByWeight = gwp / 2.20462
        this.density = density
        break
    }
  }

  setSource(settings: MaterialSourceSettings) {
    this.source = settings.source
    this.notes = settings.notes
    this.setGwp(settings.units, settings.gwp, settings.density)
  }

  static getEmpty(): Material {
    const mat = new Material('00 Other', '<none>', '')
    mat.guid = '0'
    return mat
  }

  static infoMatchesBaseline(info: MaterialInfo): boolean {
    let match = false
    const baselineInfo = CLFBaselines.find(x => x.guid === info.guid)
    if (baselineInfo){
      //check for matching data
      if(
        baselineInfo.category === info.category &&
        baselineInfo.name === info.name &&
        Math.abs(baselineInfo.gwp - info.gwp) < 0.0001 &&
        baselineInfo.units === info.units &&
        Math.abs(baselineInfo.density - info.density) < 0.001 &&
        baselineInfo.notes === info.notes
      ){
        match = true
      }
    }
    return match
  }

  isEmpty(): boolean{
    if (this.gwpByArea + this.gwpByVolume + this.gwpByWeight > 0) {
        return false;
    }
    else {
        return true;
    }
  }

  //custom method to convert to json
  toJSON(): object {
    return {
      className: this.className,
      guid: this.guid,
      category: this.category,
      name: this.name,
      source: this.source,
      gwpByArea: (this.gwpByArea * 10.7639).toString(), //convert back to m2
      gwpByVolume: (this.gwpByVolume * 35.3147).toString(), //convert back to m3
      gwpByWeight: (this.gwpByWeight * 2.20462).toString(), //convert back to kg
      density: this.density.toString(),
      transparent: this.transparent,
      notes: this.notes
    }
  }
    
}